import { ApiError } from "@core/http/ApiError";
import type { ToastTone } from "./toastNotifier";

const GENERIC_MESSAGE =
  "Ocurrió un error inesperado. Vuelve a intentarlo en unos segundos.";

/** Etiqueta del identificador de correlación, la única excepción a las
 * reglas de T7 (sin `SPEC-NNN`, sin `§`, sin códigos RF/RN/internos): su
 * razón de ser es que el operador lo copie y lo reporte. Compartida por el
 * chip del `Toast` (`PortalPrimitives.tsx`) y por `formatErrorForNotice`,
 * para los avisos en línea que no tienen chip propio — un solo texto, no dos
 * redacciones. */
export const CORRELATION_LABEL = "Código de referencia";

/**
 * SPEC-012 §5 (traducción de errores de la base): códigos que la API emite al
 * traducir una restricción del esquema — hasta 2026-09-29 salían como
 * `INTERNAL_ERROR`. No pertenecen a una sola pantalla (cualquier escritura
 * que choque con el invariante puede recibirlos, incluso por el fallback
 * global de `queryErrorFallback.ts`), así que `resolveErrorMessage` los
 * resuelve para todas; el mapeo propio de una pantalla (`codeMessages`) sigue
 * ganando si redacta el mismo código. SPEC-012 solo fija la situación, no el
 * texto: la redacción sigue el registro del portal (§4, directo y
 * operativo).
 */
export const INTEGRITY_ERROR_MESSAGES: Readonly<Record<string, string>> = {
  ACTIVE_PRIZE_EXISTS:
    "Ya hay un Gran Premio activo y solo puede haber uno a la vez. Recarga la pantalla para ver el vigente.",
  OPEN_PROGRESS_EXISTS:
    "El miembro ya tiene un conteo del Gran Premio abierto, incluido uno pendiente; no se puede abrir otro.",
  MOVEMENT_IMMUTABLE:
    "Los movimientos de puntos no se editan ni se borran. Para corregir uno, anúlalo y registra el movimiento correcto.",
  ACCESS_LOG_IMMUTABLE:
    "La bitácora de accesos no se puede modificar ni borrar.",
};

export type ResolvedError = {
  message: string;
  correlationId?: string | undefined;
};

/**
 * Resolutor único de mensajes de error del portal (T2,
 * `context/plans/qa-bug-matrix-2026-09.md` §6): ningún error puede llegar
 * sin texto a la pantalla.
 *
 * 1. `ApiError` con `code` mapeado por la pantalla (`codeMessages`) → ese
 *    texto; si la pantalla no lo mapea pero es uno de
 *    `INTEGRITY_ERROR_MESSAGES`, el texto de ese catálogo.
 * 2. `ApiError` con `problem.userMessage` (extensión opt-in del `Problem`,
 *    `contracts/parts/00-base.yaml`; primer emisor: `sendCatalogOutcome` vía
 *    `fieldRejection`, `api/src/lib/result.ts`) → ese texto, tal cual.
 *    **Esta es la única excepción a la regla de abajo**: `userMessage` no es
 *    `detail`/`title` — es un campo que el backend puebla a propósito, ya
 *    redactado en español para el operador (nunca texto técnico ni en
 *    inglés), precisamente para los rechazos donde SPEC-201 §4.4 pedía "un
 *    error genérico" y el usuario real (caso f4575316-5c75-4c27-83d0-
 *    340db0aafd9f: un taller con `noticeEmails: ["test"]`) terminaba sin
 *    saber qué campo fallaba. Cuando el rechazo además identifica el campo
 *    (`problem.errors[].field`), el formulario ya lo asoció a su input vía
 *    `applyApiFieldErrors` (`errorMessage.ts` mismo archivo) antes de llegar
 *    acá — este mensaje es el que ve el toast si no pudo asociarlo.
 * 3. `ApiError` con `code` sin mapear y sin `userMessage` → mensaje genérico
 *    más `problem.correlation` cuando el servidor lo emitió, **en el campo
 *    `correlationId`, nunca embebido en `message`** (findings de la ola 1 de
 *    `t2-foundation`, intento 2: el chip del `Toast` repetía el mismo UUID de
 *    36 caracteres que ya traía el texto). Quien renderiza un chip propio
 *    (el `Toast`) lo muestra una sola vez y etiquetado; quien solo tiene
 *    texto plano usa `formatErrorForNotice`.
 * 4. Cualquier otro `unknown` (red caída, `fetch` abortado, error de JS) →
 *    mensaje genérico sin correlación: no hay `Problem` del que inventar una.
 *
 * Fuera de `userMessage`, nunca devuelve `problem.detail` ni `problem.title`
 * crudos —`ApiError.ts:3-6` lo prohíbe explícitamente citando SPEC-201 §4.4,
 * "ningún mensaje técnico llega a la pantalla". El espíritu de esa regla se
 * respeta: lo que cambia es que ahora el backend puede marcar un mensaje
 * como ya apto para pantalla, en vez de que el portal adivine si `detail` es
 * seguro de mostrar.
 */
export function resolveErrorMessage(
  error: unknown,
  codeMessages?: Partial<Record<string, string>>,
): ResolvedError {
  if (error instanceof ApiError) {
    const mapped = error.code
      ? (codeMessages?.[error.code] ?? INTEGRITY_ERROR_MESSAGES[error.code])
      : undefined;
    if (mapped) return { message: mapped };
    const userMessage = error.problem?.userMessage;
    if (userMessage) return { message: userMessage };
    const correlationId = error.problem?.correlation;
    return correlationId
      ? { message: GENERIC_MESSAGE, correlationId }
      : { message: GENERIC_MESSAGE };
  }
  return { message: GENERIC_MESSAGE };
}

/**
 * Reusable en cualquier formulario del portal (Catálogos, y los que
 * repliquen este patrón después — ver reporte de esta corrida): vuelca al
 * formulario los rechazos de campo que la API ya identificó
 * (`problem.errors[].field`, misma extensión que `userMessage`). Devuelve
 * `true` si pudo asociar al menos un error a un input — el llamador entonces
 * NO muestra el toast genérico (el error ya está bajo el campo); si
 * devuelve `false` (el rechazo no trae `errors`, el `field` no es un
 * `string`, o no está en `knownFields`), el llamador cae a
 * `reportError`/`resolveErrorMessage` como hasta ahora. `setError` es el de
 * `react-hook-form` (`formState.errors`/`setError` de `useForm`); el `field`
 * que manda la API usa el mismo nombre plano que el formulario
 * (`noticeEmails`, `cityId`, nunca `attributes.noticeEmails` — ver
 * `catalog.service.ts`), así que no hace falta traducir nombres acá.
 *
 * `knownFields` es la lista de claves que el formulario realmente renderiza
 * (p. ej. `Object.keys(catalogEntrySchema.shape)`): sin ella, un campo que la
 * API identifica pero que el formulario no expone como input (`attributes`,
 * un atributo desconocido) contaría como "aplicado" y el toast nunca
 * mostraría el mensaje — el error desaparecería en silencio. Con la lista,
 * ese caso cae al toast igual que un rechazo sin campo.
 */
export function applyApiFieldErrors(
  error: unknown,
  setError: (field: string, error: { type: string; message: string }) => void,
  knownFields?: readonly string[],
): boolean {
  if (!(error instanceof ApiError)) return false;
  const fieldErrors = error.problem?.errors;
  if (fieldErrors === undefined || fieldErrors.length === 0) return false;
  let applied = false;
  for (const fieldError of fieldErrors) {
    if (typeof fieldError.field !== "string") continue;
    if (knownFields !== undefined && !knownFields.includes(fieldError.field))
      continue;
    setError(fieldError.field, {
      type: "server",
      message: fieldError.message ?? resolveErrorMessage(error).message,
    });
    applied = true;
  }
  return applied;
}

/**
 * Igual que `resolveErrorMessage`, pero para los avisos en línea que
 * muestran solo texto —sin el chip del `Toast`— y por lo tanto necesitan el
 * identificador de correlación embebido para que no desaparezca (`notice`
 * de `MembersScreen`, `inactivateError` de `OffersScreen`/
 * `HomeContentScreen`/`RuleEditorModal`, `assignError` de `RequestsScreen`,
 * el fallback de `CatalogsScreen` y `memberErrorMessage`). Un consumidor que
 * ya muestra el `Toast` de error (que trae su propio chip) usa
 * `resolveErrorMessage`/`reportError` directamente para no duplicar el
 * identificador.
 */
export function formatErrorForNotice(
  error: unknown,
  codeMessages?: Partial<Record<string, string>>,
): string {
  const resolved = resolveErrorMessage(error, codeMessages);
  return resolved.correlationId
    ? `${resolved.message} ${CORRELATION_LABEL}: ${resolved.correlationId}.`
    : resolved.message;
}

/** Estructura mínima que necesita `reportError`/`reportErrorWithPrefix` — la
 * misma forma que devuelve `useToast()`, sin importar `PortalPrimitives.tsx`
 * (evitaría el ciclo que `toastNotifier.ts` ya documenta). */
type ToastLike = {
  show: (
    message: string,
    tone?: ToastTone,
    options?: { correlationId?: string | undefined },
  ) => void;
};

/**
 * T2 (`t2-screens`, qa-bug-matrix-2026-09.md §4.1/§4.4): las pantallas
 * repetían el mismo trío "resolver → toast de error → marcar atendido"
 * (jscpd lo marcó como clon entre Beneficios/Ofertas/Inicio/Gran Premio).
 * Un `onError` que solo necesita el mensaje resuelto usa esta función en
 * vez de repetir las tres líneas.
 */
export function reportError(
  toast: ToastLike,
  error: unknown,
  codeMessages?: Partial<Record<string, string>>,
): ResolvedError {
  const resolved = resolveErrorMessage(error, codeMessages);
  toast.show(resolved.message, "error", {
    correlationId: resolved.correlationId,
  });
  markErrorHandled(error);
  return resolved;
}

/** `onError` de un editor que recibe del formulario su propio volcado de
 * rechazos por campo (`applyFieldErrors`, que envuelve `applyApiFieldErrors`
 * con el `setError` del editor): si el error quedó bajo un input, se marca
 * atendido y no hay toast; si no, `reportError` como siempre. Compartido por
 * Beneficios y Promociones, que cablean el formulario así en vez de con
 * `useApiFieldErrors`. */
export function reportUnlessFieldError(
  toast: ToastLike,
  error: unknown,
  applyFieldErrors: (error: unknown) => boolean,
  codeMessages?: Partial<Record<string, string>>,
): void {
  if (applyFieldErrors(error)) {
    markErrorHandled(error);
    return;
  }
  reportError(toast, error, codeMessages);
}

/** Igual que `reportError`, pero para un `onError` que necesita nombrar el
 * registro afectado antes del mensaje resuelto (p. ej. "No se pudo
 * reactivar «X». <mensaje>") — no acepta `codeMessages`: estos sitios usan
 * siempre el genérico + correlación. */
export function reportErrorWithPrefix(
  toast: ToastLike,
  prefix: string,
  error: unknown,
): ResolvedError {
  const resolved = resolveErrorMessage(error);
  toast.show(`${prefix} ${resolved.message}`, "error", {
    correlationId: resolved.correlationId,
  });
  markErrorHandled(error);
  return resolved;
}

// T2 (2026-09-19, corrección post-cierre del intento 1 de `t2-foundation`):
// TanStack Query v5 invoca `MutationCache.onError` incondicionalmente y
// ANTES que el `onError` del sitio de la llamada (`.mutate(input, {
// onError })`) — verificado contra el código fuente instalado
// (`@tanstack/query-core/build/modern/mutation.js` y `mutationObserver.js`):
// son tres callbacks independientes que no se enteran entre sí. Sin esto, el
// fallback global de `main.tsx` duplicaba el aviso de cualquier pantalla que
// ya resolvía su propio mensaje —y el genérico salía primero, en el peor
// orden—. La solución no es que cada pantalla declare un `meta` (eso es
// exactamente el patrón pantalla-por-pantalla que este paso existe para
// evitar): el `onError` de la mutación corre en el mismo tick, justo
// después del de la caché, así que el fallback se difiere un macrotask
// (`setTimeout(fn, 0)` en `main.tsx`) y consulta este `WeakSet` antes de
// hablar. Un `onError` que ya resolvió su mensaje específico —con este
// mismo `resolveErrorMessage` o con su propio mapeo— llama a
// `markErrorHandled(error)` justo después de mostrar su toast; si nadie lo
// marcó para cuando el macrotask corre, habla el fallback. `t2-screens`
// cablea esto al migrar cada pantalla; no hace falta declarar nada más.
const handledErrors = new WeakSet<WeakKey>();

export function markErrorHandled(error: unknown): void {
  if (typeof error === "object" && error !== null) {
    handledErrors.add(error);
  }
}

export function isErrorHandled(error: unknown): boolean {
  return (
    typeof error === "object" && error !== null && handledErrors.has(error)
  );
}
